import { useCallback, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useId } from '../../hooks/useId.js';
import type { TreeNodeData, TreeProps } from './types.js';

/** Flatten the visible slice of the tree — skips children of collapsed
 *  nodes. Used for keyboard navigation: ↑/↓ move between the *visible*
 *  rows, not the full recursive set. */
function flattenVisible(
  nodes: ReadonlyArray<TreeNodeData>,
  expanded: ReadonlySet<string>,
  depth = 0,
  acc: Array<{ node: TreeNodeData; depth: number }> = [],
): Array<{ node: TreeNodeData; depth: number }> {
  for (const n of nodes) {
    acc.push({ node: n, depth });
    if (n.children !== undefined && expanded.has(n.id)) {
      flattenVisible(n.children, expanded, depth + 1, acc);
    }
  }
  return acc;
}

/** WAI-ARIA Tree pattern.
 *  https://www.w3.org/WAI/ARIA/apg/patterns/treeview/
 *
 *  Keyboard:
 *    ↓ / ↑     next / previous visible row
 *    →          expand (or move to first child if already expanded)
 *    ←          collapse (or move to parent if already collapsed)
 *    Home/End   first / last visible
 *    Enter      select / toggle expand
 *    Space      select
 *
 *  Implementation notes:
 *  - Roving tabindex (only one node is `tabindex=0`) keeps the tree out
 *    of the Tab cycle between nodes.
 *  - Focus is managed imperatively on the row element — children can
 *    include non-focusable badges without disturbing the model.
 *  - Selection and expansion are independent: selecting a folder keeps
 *    its state; clicking the chevron toggles expand without selecting.
 */
export function Tree(props: TreeProps): ReactNode {
  const {
    nodes,
    expanded,
    defaultExpanded,
    onExpandedChange,
    selected,
    defaultSelected,
    onSelect,
    indentStep = '16px',
    className,
    'aria-label': ariaLabel,
  } = props;

  const [expandedSet, setExpandedSet] = useControllableState<ReadonlySet<string>>({
    defaultValue: defaultExpanded ?? new Set<string>(),
    ...(expanded !== undefined ? { value: expanded } : {}),
    ...(onExpandedChange !== undefined ? { onChange: onExpandedChange } : {}),
  });
  const [selectedId, setSelectedId] = useControllableState<string | undefined>({
    defaultValue: defaultSelected,
    ...(selected !== undefined ? { value: selected } : {}),
  });

  const rootId = useId('tree');
  const containerRef = useRef<HTMLUListElement>(null);

  const toggleExpanded = useCallback(
    (id: string) => {
      const next = new Set(expandedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpandedSet(next);
    },
    [expandedSet, setExpandedSet],
  );

  const visible = useMemo(() => flattenVisible(nodes, expandedSet), [nodes, expandedSet]);

  const focusRow = useCallback((id: string) => {
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-node-id="${id}"]`);
    el?.focus();
  }, []);

  const findParentOf = useCallback(
    (id: string): TreeNodeData | null => {
      function search(
        nodes: ReadonlyArray<TreeNodeData>,
        parent: TreeNodeData | null,
      ): TreeNodeData | null {
        for (const n of nodes) {
          if (n.id === id) return parent;
          if (n.children !== undefined) {
            const found = search(n.children, n);
            if (found !== null) return found;
          }
        }
        return null;
      }
      return search(nodes, null);
    },
    [nodes],
  );

  const onKey = useCallback(
    (e: KeyboardEvent<HTMLElement>, node: TreeNodeData) => {
      const idx = visible.findIndex((v) => v.node.id === node.id);
      const isFolder = node.children !== undefined;
      const isOpen = expandedSet.has(node.id);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = visible[idx + 1];
        if (next !== undefined) focusRow(next.node.id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = visible[idx - 1];
        if (prev !== undefined) focusRow(prev.node.id);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (isFolder && !isOpen) {
          toggleExpanded(node.id);
        } else if (isFolder && isOpen) {
          const next = visible[idx + 1];
          if (next !== undefined) focusRow(next.node.id);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (isFolder && isOpen) {
          toggleExpanded(node.id);
        } else {
          const parent = findParentOf(node.id);
          if (parent !== null) focusRow(parent.id);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        if (visible[0] !== undefined) focusRow(visible[0].node.id);
      } else if (e.key === 'End') {
        e.preventDefault();
        const last = visible[visible.length - 1];
        if (last !== undefined) focusRow(last.node.id);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (node.disabled !== true) {
          setSelectedId(node.id);
          onSelect?.(node.id, node);
          if (isFolder) toggleExpanded(node.id);
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        if (node.disabled !== true) {
          setSelectedId(node.id);
          onSelect?.(node.id, node);
        }
      }
    },
    [visible, expandedSet, toggleExpanded, focusRow, findParentOf, onSelect, setSelectedId],
  );

  /* If `selected` was set but points at a non-existent node id, fall
   * back to making the first visible node tabbable — otherwise the
   * whole tree becomes unreachable by keyboard. */
  const selectedExists = selectedId !== undefined && visible.some((v) => v.node.id === selectedId);
  const fallbackTabId = visible[0]?.node.id;

  const renderNode = (node: TreeNodeData, depth: number): ReactNode => {
    const isFolder = node.children !== undefined;
    const isOpen = expandedSet.has(node.id);
    const isSelected = selectedId === node.id;
    /* Roving-tabindex: selected (if present in tree) or first visible node. */
    const tabbable = selectedExists ? isSelected : fallbackTabId === node.id;

    return (
      <li
        key={node.id}
        role="treeitem"
        aria-expanded={isFolder ? isOpen : undefined}
        aria-selected={isSelected}
        aria-disabled={node.disabled}
        aria-level={depth + 1}
        className="vq-tree__item"
        data-node-id={node.id}
        data-depth={depth}
        data-state={isSelected ? 'selected' : undefined}
        tabIndex={tabbable ? 0 : -1}
        onKeyDown={(e) => {
          onKey(e, node);
        }}
        style={
          { '--vq-tree-indent': `calc(${String(depth)} * ${indentStep})` } as React.CSSProperties
        }
        onClick={(e) => {
          e.stopPropagation();
          if (node.disabled === true) return;
          setSelectedId(node.id);
          onSelect?.(node.id, node);
        }}
      >
        <div className="vq-tree__row">
          {isFolder ? (
            <button
              type="button"
              tabIndex={-1}
              className="vq-tree__chevron"
              aria-hidden="true"
              onClick={(e) => {
                e.stopPropagation();
                if (node.disabled !== true) toggleExpanded(node.id);
              }}
              data-open={isOpen ? 'true' : 'false'}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <polyline
                  points="4 2 8 6 4 10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <span className="vq-tree__chevron vq-tree__chevron--leaf" aria-hidden="true" />
          )}
          {node.icon !== undefined && (
            <span className="vq-tree__icon" aria-hidden="true">
              {node.icon}
            </span>
          )}
          <span className="vq-tree__label">{node.label}</span>
          {node.meta !== undefined && <span className="vq-tree__meta">{node.meta}</span>}
        </div>
        {isFolder && isOpen && node.children !== undefined && (
          <ul role="group" className="vq-tree__children">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <ul
      ref={containerRef}
      role="tree"
      id={rootId}
      className={['vq-tree', className].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
      data-component="Tree"
    >
      {nodes.map((n) => renderNode(n, 0))}
    </ul>
  );
}
